import api from './api';

/**
 * 虚拟人老师选择的本地工具
 * ============================================================
 *
 * 为什么要有本地兜底（FALLBACK_TUTORS）：
 *   老师列表来自后端 /api/tutors，但在离线、后端未启动、或请求失败时，
 *   如果列表为空，用户会看到「没有可选老师」的死界面。
 *   这里保留一份与 config/tutors.js 对齐的最小兜底数据，
 *   保证弱网下依然能进入对话（人格由后端决定，兜底只影响选择界面展示）。
 */

const SELECTED_TUTOR_KEY = 'selectedTutorId';

/** 兜底老师列表（与后端 config/tutors.js 保持一致，仅用于展示） */
export const FALLBACK_TUTORS = [
  {
    id: 'xingshi',
    name: '小醒狮',
    avatar: '🦁',
    tagline: '岭南醒狮化身，活泼友善的专业粤语老师',
    voice: 'auto',
    rate: 1,
    specialty: ['日常对话', '俚语俗语', '岭南文化'],
    greeting: '你好呀！我係小醒狮，今日想同你倾啲咩呀？',
    // 与 config/tutors.js 保持一致，保证离线时也能显示真实立绘
    portrait: '/images/logo-mascot.jpg',
    mouthPosition: { x: 45, y: 60, w: 13, h: 8 },
  },
  {
    id: 'aqiang',
    name: '阿强',
    avatar: '🧑‍🔧',
    tagline: '幽默风趣的香港街坊，专治「唔敢开口」',
    voice: 'Rocky',
    rate: 1,
    specialty: ['街市买餸', '茶餐厅', '地道俚语', '吹水聊天'],
    greeting: '喂！你好呀，我係阿强。得闲同我吹下水啦，讲错都唔怕㗎！',
  },
  {
    id: 'aqing',
    name: '阿清',
    avatar: '👩‍🏫',
    tagline: '甜美的港妹闺蜜，温柔细心地陪你练口语',
    voice: 'Kiki',
    rate: 1,
    specialty: ['日常会话', '购物点餐', '职场沟通', '发音纠正'],
    greeting: '你好呀！我係阿清。唔使紧张，当同朋友倾偈就得嘞，我哋慢慢嚟～',
  },
];

export const DEFAULT_TUTOR_ID = 'aqing';

/**
 * 读取用户当前选定的老师 ID
 * @returns {string} 老师 ID（一定有值，回退到默认老师）
 */
export function getSelectedTutorId() {
  try {
    return localStorage.getItem(SELECTED_TUTOR_KEY) || DEFAULT_TUTOR_ID;
  } catch (e) {
    return DEFAULT_TUTOR_ID;
  }
}

/**
 * 保存用户选定的老师 ID
 * @param {string} tutorId
 */
export function setSelectedTutorId(tutorId) {
  try {
    localStorage.setItem(SELECTED_TUTOR_KEY, tutorId);
  } catch (e) {
    console.warn('[tutor] 保存老师选择失败:', e?.message);
  }
}

/**
 * 拉取老师列表
 * @returns {Promise<{ tutors: Array, defaultTutorId: string, fromFallback: boolean }>}
 */
export async function fetchTutors() {
  try {
    const response = await api.get('/api/tutors');
    const data = response.data?.data || response.data;
    const tutors = data?.tutors;

    if (Array.isArray(tutors) && tutors.length > 0) {
      return {
        tutors,
        defaultTutorId: data.defaultTutorId || DEFAULT_TUTOR_ID,
        fromFallback: false,
      };
    }
  } catch (error) {
    console.warn('[tutor] 拉取老师列表失败，使用本地兜底:', error?.message);
  }

  return {
    tutors: FALLBACK_TUTORS,
    defaultTutorId: DEFAULT_TUTOR_ID,
    fromFallback: true,
  };
}

/**
 * 按 ID 取老师（先查后端结果，查不到再查兜底）
 * @param {Array} tutors - 已加载的老师列表
 * @param {string} tutorId
 * @returns {Object|null}
 */
export function pickTutor(tutors, tutorId) {
  if (Array.isArray(tutors) && tutors.length > 0) {
    const found = tutors.find((t) => t.id === tutorId);
    if (found) return found;
    return tutors[0];
  }
  return FALLBACK_TUTORS.find((t) => t.id === tutorId) || FALLBACK_TUTORS[0];
}
